/**
 * OnestoneConfig — the master persisted-config aggregate — plus retention
 * guardrails (tmp, claude-home, ops log, channel log, trash). Composes every
 * per-subsystem config from ./config and ./config-context.
 */
import type { ChannelName, ToolCategory } from './channels.js';
import type {
  AgencyConfig,
  AgentConfig,
  BashSafety,
  BrowserConfig,
  ChannelSettings,
  ClaudeCodeConfig,
  ExternalMcpServerConfig,
  CloudLinkConfig,
  McpClientConfig,
  RetrievalConfig,
  SchedulerConfig,
  ToolIoConfig,
  ToolPolicy,
} from './config.js';
import type {
  AlertConfig,
  ContextConfig,
  SituationSectionCaps,
  SituationSliceLimits,
  SummarizeConfig,
} from './config-context.js';
import type { NetworkConfig } from './network.js';
import { resolveSection } from '../settings/registry.js';
import { CLAUDE_HOME_SETTINGS, RETENTION_SETTINGS, TMP_SETTINGS } from '../settings/sections.js';
import type { ProviderConfig } from './provider.js';

/** Logger floors, lowest first. */
export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

export interface OnestoneConfig {
  /** Each channel's model setup — source, model, effort, temperature, max output, budget, fallbacks (Settings → Model). */
  channels?: Partial<Record<ChannelName, ChannelSettings>> | undefined;
  /** Subscription runtime settings (auth mode, CLI command, token). */
  claudeCode?: ClaudeCodeConfig | undefined;
  tools?: {
    policies?: Record<string, Partial<ToolPolicy>>;
    /** Bash safety posture (see {@link BashSafety}). 'off' (default) = fully-
     *  autonomous bash; 'block-dangerous' denies flagged commands (recursive root
     *  deletes, curl|sh droppers, disk wipes, fork bombs); 'approve-dangerous' /
     *  'approve-all' surface an inline Accept/Deny card. Enforced for ALL callers
     *  in `src/agent/tools/policy.ts` — the indirect-prompt-injection mitigation. */
    bashSafety?: BashSafety;
    /** Let `bash` run commands that only do what a dedicated tool does
     *  (reading, searching, listing, git reads, literal edits, test runs).
     *  Off (default): they are refused with the tool to use instead
     *  (`src/agent/tools/bashToolWork.ts`). Dashboard-only, like `bashSafety`. */
    bashForToolWork?: boolean;
  };
  /** Unified agent-agency gate (MA-350) — interactive-agent autonomy level +
   *  universal deny-list. Absent → 'full' (fully autonomous, today's default). */
  agency?: AgencyConfig;
  scheduler?: Partial<SchedulerConfig>;
  /** Browser session pool tunables — applied live via applyBrowserOptions. */
  browser?: Partial<BrowserConfig>;
  /** Agent-loop tunables — applied live via applyAgentOptions (turns, headroom, burst, tool-result cap). */
  agent?: Partial<AgentConfig>;
  /** Retrieval-depth tunables — applied live via applyRetrievalOptions (scan ceiling, result/page defaults, situation injection). */
  retrieval?: Partial<RetrievalConfig>;
  /** Tool-I/O content-plane tunables — applied live via applyToolIoOptions (result cap, retention days; 0 = unbounded). */
  toolIo?: Partial<ToolIoConfig>;
  /** The context-fill warning (only the operator condenses a conversation). Read per-turn in chat.ts. */
  context?: Partial<ContextConfig>;
  /** Summarize-pipeline tunables — chunk size, multi-chunk strategy, map concurrency. Read per-job in startSummarizeJob. */
  summarize?: Partial<SummarizeConfig>;
  /** Situation-block tunables — per-section overflow caps + pre-query slice limits. Applied live via applySituationOptions. */
  situation?: { sectionCaps?: Partial<SituationSectionCaps>; sliceLimits?: Partial<SituationSliceLimits> };
  /** Alert engine tunables — thresholds for tool error rate, routine failures, cost ceiling. */
  alerts?: Partial<AlertConfig> | undefined;
  providers?: ProviderConfig;
  /** This desktop's link to the cloud account (`src/api/cloudLink.ts`, `cloudSync.ts`). */
  cloud?: CloudLinkConfig | undefined;
  /** External MCP servers the operator connected (Connections → External MCP Servers). */
  externalMcp?: ExternalMcpServerConfig[] | undefined;
  /** How OneStone's client treats those servers — reconnect backoff, instructions cap. Applied live. */
  mcpClient?: Partial<McpClientConfig> | undefined;
  /**
   * Per-channel tool-category scoping. Currently consumed by the `subagent`
   * channel only (`spawnAgentDispatch.ts`) — narrows the read-only tool
   * surface a sub-agent worker sees to the selected categories, layered on
   * TOP of the `mutates === false` security floor (never replaces it).
   * Unset → `DEFAULT_SUBAGENT_TOOL_CATEGORIES`. MA-295 (2026-08-01).
   */
  channelToolCategories?: Partial<Record<ChannelName, ToolCategory[]>> | undefined;
  /** Network access — server bind host + API tokens for non-loopback (mobile) clients. */
  network?: NetworkConfig | undefined;
  /** Whether first-run onboarding is done. The profile it collects lives in the operator profile. */
  onboarding?:
    | {
        completed: boolean;
        completedAt?: string | undefined;
      }
    | undefined;
  /**
   * Logger floor. `LOG_LEVEL` env wins for the current process when set, so
   * a developer doing `LOG_LEVEL=debug npm run dev` is never silently
   * overridden by Settings. Unset → 'info'.
   */
  logLevel?: LogLevel | undefined;
  /**
   * Agent scratch settings. `tmp.retentionHours` controls how old a file in
   * `~/.onestone/tmp/` can be before the cleanup sweep deletes it.
   * Default 48h. Clamped to [TMP_RETENTION_MIN_HOURS, TMP_RETENTION_MAX_HOURS]
   * (1h–720h) at read time.
   */
  tmp?: {
    retentionHours?: number;
  };
  /**
   * Claude Code CLI session-artifact retention. Controls how old a file under
   * the allowlisted `~/.onestone/claude-home/` subdirs (session-env, sessions,
   * shell-snapshots, tasks, projects, backups) can be before the cleanup sweep
   * deletes it. Protected files (.claude.json, .credentials.json,
   * mcp-needs-auth-cache.json) are never visited.
   * Default 168h (7 days). Clamped to [CLAUDE_HOME_RETENTION_MIN_HOURS,
   * CLAUDE_HOME_RETENTION_MAX_HOURS] (24h–8760h) at read time.
   */
  claudeHome?: {
    retentionHours?: number;
  };
  /**
   * Operations log retention — how many days of ops events (tool calls, turns,
   * routine runs, errors) are kept on disk. The ops log is the data source for
   * analytics, insights, and the activity feed. Default 30 days. Clamped to 7–365.
   */
  opsRetentionDays?: number;
  /**
   * Trash retention — how many days anything in the one OneStone trash
   * (deleted records and replaced versions, ADR-0003) stays recoverable before
   * the daily purge permanently removes it. Default 7 days. Clamped to 1–365.
   * Set to 0 to disable the automatic purge (the operator can still purge
   * items from the Trash panel).
   */
  trashRetentionDays?: number;
  /** Folder names whose files agent changes don't keep in the trash (`RETENTION_SETTINGS.trashSkipFolders`). */
  trashSkipFolders?: string[];
}

/** Hours a file in `~/.onestone/tmp/` is kept before the cleanup sweep removes it. */
export function resolveTmpRetentionHours(config: OnestoneConfig | null | undefined): number {
  return resolveSection(TMP_SETTINGS, config?.tmp).retentionHours;
}

/** Hours a Claude CLI working file under `~/.onestone/claude-home/` is kept. */
export function resolveClaudeHomeRetentionHours(config: OnestoneConfig | null | undefined): number {
  return resolveSection(CLAUDE_HOME_SETTINGS, config?.claudeHome).retentionHours;
}

/** Days of operations-log history kept. */
export function resolveOpsRetentionDays(config: OnestoneConfig | null | undefined): number {
  return resolveSection(RETENTION_SETTINGS, config ?? {}).opsRetentionDays;
}

/** Days anything stays in the OneStone trash (ADR-0003); 0 = no automatic purge. */
export function resolveTrashRetentionDays(config: OnestoneConfig | null | undefined): number {
  return resolveSection(RETENTION_SETTINGS, config ?? {}).trashRetentionDays;
}

/** Folder names whose files agent changes don't keep in the trash (Settings → Workspace & Cleanup). */
export function resolveTrashSkipFolders(config: OnestoneConfig | null | undefined): readonly string[] {
  return resolveSection(RETENTION_SETTINGS, config ?? {}).trashSkipFolders;
}
